/**
 * How a dev build names itself against the last release.
 *
 * Staging mints every product number as a release tag, and dev shows that same
 * number while it can hold more code. A dev build adds how much more:
 * `v10.01.011-dev.3` is release 10.01.011 plus 3 merges into dev that staging
 * does not have yet, web and API together. One PR is one merge however many
 * commits it holds. With none it shows the plain number, which then means the
 * same code as staging.
 *
 * Each side counts in scripts/release.mjs (buildIdentity). The web count reaches
 * the bundle through vite.config.ts; the API reports its own through
 * GET /v1/health/version, so a backend merge moves the number without a web
 * rebuild.
 */

export interface BuildIdentity {
  /** The newest release tag in this side's repo, e.g. `v10.01.011`; null before the first. */
  release: string | null
  /** Merges into dev that staging does not have yet (one per PR). */
  ahead: number
  /** Short commit hash. */
  commit: string
  /** ISO timestamp of the build. */
  builtAt: string
}

/**
 * `-dev.3` for 3 merges into dev past staging, web and API together; '' when
 * there are none. A side with no release tag (a clone without tags) adds
 * nothing: its count may be the whole history, which says nothing.
 */
export function devVersionSuffix(...sides: Array<BuildIdentity | null>): string {
  const merges = sides.reduce((sum, side) => sum + (side?.release ? side.ahead : 0), 0)
  return merges > 0 ? `-dev.${merges}` : ''
}

/**
 * One tooltip line, e.g. `web a1b2c3d, 2 merges past v10.01.011, built 2026-09-30 14:02 UTC`.
 * UTC so two people comparing builds across time zones read the same stamp.
 */
export function describeBuild(component: 'web' | 'api', identity: BuildIdentity): string {
  const parts = [`${component} ${identity.commit}`]
  if (identity.release) {
    const merges = identity.ahead === 1 ? 'merge' : 'merges'
    parts.push(identity.ahead > 0 ? `${identity.ahead} ${merges} past ${identity.release}` : `same as ${identity.release}`)
  }
  const built = identity.builtAt.slice(0, 16).replace('T', ' ')
  if (built) parts.push(`built ${built} UTC`)
  return parts.join(', ')
}

/** The API's GET /v1/health/version body, or null when it is not a build identity. */
export function parseBuildIdentity(body: unknown): BuildIdentity | null {
  if (!body || typeof body !== 'object') return null
  const { release, ahead, commit, builtAt } = body as Record<string, unknown>
  if (release !== null && typeof release !== 'string') return null
  if (typeof ahead !== 'number' || !Number.isInteger(ahead) || ahead < 0) return null
  if (typeof commit !== 'string' || !commit) return null
  if (typeof builtAt !== 'string') return null
  return { release, ahead, commit, builtAt }
}
